import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, Tooltip, Typography} from "@mui/material";
import CCButton from "../components/Common/CCButton";
import css_buttons from "../components/Common/css/CCButton.module.scss";
import EnterpriseObject from "../eoec/eo/EnterpriseObject";
import enterpriseObjectClassFactory from "../eoec/EnterpriseObjectClassFactory";
import ObjectRevisionTableApplet from "../eoec/applet/impl/Foundation/ObjectRevisionTableApplet";
import ObjectFilterSidebar from "../components/Foundation/ObjectFilterSidebar";
import CreateProject from "../components/Project/CreateProject";
import CreateObject from "../components/Foundation/CreateObject";
import EventBus from "../util/EventBus";
import {hasPrivilege, useSessionContext} from "../util/SessionContext";
import {availableModes} from "../util/VisibilityModes";
import {ObjectStatistics, loadObjectStatistics, withDescendants} from "../util/objectStatistics";

import {ObjectClassification, objectPrivilegeCode} from "../util/objectClassifications";
import {defaultStatusesFor, statusVocabularyFor} from "../util/objectStatuses";
import PageHeader from "../components/Common/PageHeader";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import {Blue3, Grey2, Grey6, Grey17, White} from "../theme/styles";
import {makeCresAxiosFoundationAPI} from "../util/AxiosFoundationAPI";
import {Facet} from "../components/Foundation/ObjectFilterSidebar";
import {withKinds} from "../util/formFieldRanges";
import {AutoAwesomeOutlined} from "@mui/icons-material";
import {prepareBuffer} from "../util/Buffer";
import AskAboutAttachments from "../components/Foundation/AskAboutAttachments";
import SearchAttachments from "../components/Foundation/SearchAttachments";
import SearchRecords from "../components/Foundation/SearchRecords";

// What a list opens showing. Not a hidden rule: the sidebar draws these two ticked, so the filter
// the user can see is the filter that is applied.
//
// BELOW EVERY IMPORT, deliberately. A const between imports trips eslint's import/first, which CRA
// treats as a build error -- it took the dev server down rather than warning.


/**
 * The list page for ANY first-class object, driven by the classification handed to it.
 *
 * No classification has a table of its own: every one is ObjectRevision rows filtered by
 * ObjectClassification, which is why the EC gets an explicit filter here rather than the component
 * defaulting to one -- one component backs all nine, and a baked-in default would silently mis-scope
 * eight of them.
 *
 * Filtering lives entirely in the left pane. The Published / Unpublished / All tabs that used to sit
 * opposite the visibility selector are gone: they were a coarser version of the same thing, and
 * having both meant two controls that could disagree about which statuses were showing.
 */

const tabStyle = (active: boolean): React.CSSProperties => ({
    cursor: "pointer",
    paddingBottom: 6,
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    color: active ? Grey17 : Grey6,
    borderBottom: active ? `2px solid ${Blue3}` : "2px solid transparent",
});



/**
 * How many records one question may read, mirrored from the service's own MAX_RECORDS.
 *
 * A COPY, AND THE SERVER IS STILL THE AUTHORITY. It refuses CC-ATAI-MANY either way; this exists
 * so a reader who has just ticked thirty rows is told the number before asking rather than after
 * being refused. A client cap that disagreed with the server would only ever be the stricter of
 * the two here, which fails safe.
 */
const MAX_ASK_RECORDS = 25;


/** A record the picker hands back: the revision the grid showed, and the logical object it is. */
export type PickedRecord = {RevisionUSID: string; ObjectUSID: string | null; ObjectID: string | null;
                            Name: string | null; Status: string | null};

/**
 * PICKER MODE. Ed, 2026-10-28: "I need a means of doing complex filtering and full text searching
 * on an object ... There may be thousands of purchase orders and I need to get the list narrowed.
 * So a popup that offered like a contained purchase order screen with the filter by options on
 * the left and the persona selectors (where permissions allow) and pagination." That screen is
 * this page. Given a picker, it draws without the blue header and the New button, a row click
 * ticks the row instead of opening it, and the Ask action is not drawn (the host's footer carries
 * Cancel and Select). Filter
 * By, the visibility tabs, the search boxes and the grid's paging are exactly the page's.
 */
export type Picker = {
    multiple?: boolean;
    /** Told whenever the ticked set changes, with the records captured for it. The HOST draws
     *  Cancel and Select -- in its own footer, bottom right (Ed, 2026-10-28) -- so the page draws
     *  no action of its own in picker mode. */
    onSelectionChange: (picked: PickedRecord[]) => void;
};

export default function ObjectList({classification, picker}: {classification: ObjectClassification;
                                                               picker?: Picker}) {
    const sessionContext = useSessionContext();
    const {code, plural, icon: Icon} = classification;

    // DRAFT AND RELEASED ARE TICKED TO BEGIN WITH -- or OPEN, for a project, whose only words are
    // OPEN and CLOSED. Those are the records somebody is working on or working from; superseded,
    // cancelled, deprecated and void revisions are history, and showing them by default made a list
    // of 207 rows where 166 were live. Untick them and everything shows -- no filter still means no
    // filter. The vocabulary the sidebar offers comes from the same function as the default ticks,
    // so a classification cannot be handed one and not the other.
    const [selectedStatuses, setSelectedStatuses] =
        useState<Set<string>>(new Set(defaultStatusesFor(code)));
    // NOTHING TICKED TO BEGIN WITH, unlike the statuses above. A default status is a judgement about
    // which records are interesting; a default TYPE would be a judgement about which of the
    // customer's own types matter, which is not the product's to make. No ticks means every type.
    const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
    // The Foundation client, which attaches the active persona -- the facet counts are
    // scoped by it server-side, so a call without it is answered with nothing.
    const facetApi = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);
    // The form questions this classification can be filtered by, and which answers are ticked.
    const [facets, setFacets] = useState<Facet[]>([]);
    const [facetsAvailable, setFacetsAvailable] = useState<number>(0);
    const [facetsTruncated, setFacetsTruncated] = useState<boolean>(false);
    const [selectedFacets, setSelectedFacets] = useState<Record<string, Set<string>>>({});
    // Typed range bounds, keyed by field code. Held as typed, so a half-entered number is shown
    // back to the reader exactly as they typed it; the EC drops a bound it cannot use.
    const [facetRanges, setFacetRanges] = useState<Record<string, {from: string; to: string}>>({});
    // THE RECORDS TICKED TO ASK ABOUT. Held here rather than in the grid because it has to survive
    // paging -- somebody picking four documents out of two hundred will cross a page boundary doing
    // it -- and because the toolbar that acts on the set sits above the grid, so one set with one
    // owner is the only arrangement where the count and the question can agree.
    const [askSelection, setAskSelection] = useState<Set<string>>(new Set());
    const [newestOnly, setNewestOnly] = useState<boolean>(false);
    const [daysOldEnabled, setDaysOldEnabled] = useState<boolean>(false);
    // Held as typed rather than as a number, so the box can be empty or mid-edit without the filter
    // reading it as 0 and matching nothing.
    const [daysOld, setDaysOld] = useState<string>("");
    const [statistics, setStatistics] = useState<ObjectStatistics | null>(null);
    const [creating, setCreating] = useState<boolean>(false);

    // Built once. Rebuilding it on every render would hand the grid a fresh EC each time and it would
    // re-query forever.
    const objectsEO: EnterpriseObject | undefined = useMemo(() => {
        const eo = enterpriseObjectClassFactory("ObjectRevision");
        // Scope BEFORE the grid mounts. PlatformTable's mount query runs before any effect here, so
        // setting the filter in a useEffect would let the first load through unscoped -- it would
        // briefly show every other classification's records alongside this one's.
        const ec: any = eo?.getEnterpriseComponent("ObjectRevision");
        ec?.setClassificationFilter(code);
        // Set HERE for the same reason the classification is: PlatformTable's mount query runs
        // before any effect on this page, so a default applied in an effect would let the first
        // load through unfiltered and then visibly re-query.
        ec?.setStatusesFilter(defaultStatusesFor(code));
        return eo;
    }, [code]);

    const refreshStatistics = useCallback(() => {
        loadObjectStatistics(code, sessionContext).then(setStatistics);
    }, [code, sessionContext]);

    useEffect(() => {
        refreshStatistics();
    }, [refreshStatistics]);

    // Re-query AND recount whenever the active persona changes: visibility scopes which records
    // resolve at all, so counts computed under the previous persona would be wrong.
    useEffect(() => {
        const onChangeVisibility = () => {
            objectsEO?.getEnterpriseComponent("ObjectRevision")?.invalidate();
            refreshStatistics();
        };
        EventBus.visibility.addListener("change-visibility", onChangeVisibility);
        return () => {
            EventBus.visibility.removeListener("change-visibility", onChangeVisibility);
        };
    }, [objectsEO, refreshStatistics]);

    // Newest (the last week) and Days old (the last N days) both cut on the creation date, so they
    // resolve to ONE condition. Two ">=" conditions AND-ed together are just the later of the two
    // cutoffs, so the tighter window wins rather than the rows disappearing when both are ticked.
    const createdAfter = (newest: boolean, daysEnabled: boolean, days: string): string | null => {
        const cutoffs: number[] = [];
        if (newest) {
            cutoffs.push(Date.now() - 7 * 24 * 60 * 60 * 1000);
        }
        const n = parseInt(days, 10);
        if (daysEnabled && Number.isFinite(n) && n > 0) {
            cutoffs.push(Date.now() - n * 24 * 60 * 60 * 1000);
        }
        return cutoffs.length > 0 ? new Date(Math.max(...cutoffs)).toISOString() : null;
    };

    const applyFilters = useCallback((statuses: Set<string>, types: Set<string>, newest: boolean, daysEnabled: boolean, days: string, chosen?: Record<string, Set<string>>, ranges?: Record<string, {from: string; to: string}>) => {
        const ec: any = objectsEO?.getEnterpriseComponent("ObjectRevision");
        ec?.setStatusesFilter(Array.from(statuses));
        // TICKING A PARENT MEANS ITS DESCENDANTS. Expanded once, here, and the expansion is what
        // reaches both the grid and the facet counts -- so the numbers in the pane and the rows in
        // the list can never describe different sets.
        ec?.setObjectTypesFilter(withDescendants(statistics?.types ?? [], types));
        ec?.setCreatedAfterFilter(createdAfter(newest, daysEnabled, days));
        // The form's own questions. Passed through as given -- the EC turns each into a FormField
        // member and the server does the OR-within / AND-between grouping.
        ec?.setFormFieldFilters(chosen ?? selectedFacets);
        // RANGES ARE A SEPARATE CALL because they are a separate predicate: a value filter is
        // containment over the jsonb document, a range is a comparison over the typed projection.
        // The kind travels with each one so the server knows which column it means.
        ec?.setFormFieldRangeFilters(withKinds(ranges ?? facetRanges, facets));
        ec?.invalidate();
    }, [objectsEO, selectedFacets, facetRanges, facets, statistics]);

    // `fieldCode`, not `code`: `code` in this component is the CLASSIFICATION, and shadowing it
    // here would make a future edit inside this function filter the wrong thing entirely.
    const toggleFacet = (fieldCode: string, value: string) => {
        const next: Record<string, Set<string>> = {};
        for (const [key, set] of Object.entries(selectedFacets)) next[key] = new Set(set);
        const set = next[fieldCode] ?? new Set<string>();
        if (set.has(value)) set.delete(value); else set.add(value);
        next[fieldCode] = set;
        setSelectedFacets(next);
        applyFilters(selectedStatuses, selectedTypes, newestOnly, daysOldEnabled, daysOld, next);
    };

    // TYPING A BOUND, SETTLED. Days old re-queries on every keystroke and gets away with it: one
    // box, and a number nobody types more than two digits into. A range has two boxes over a span
    // like 250 to 5000, so the same code would fire four queries to ask one question. Waiting for
    // the typing to stop asks it once.
    const rangeTimer = useRef<any>(null);
    const changeFacetRange = (fieldCode: string, which: "from" | "to", value: string) => {
        const next = {...facetRanges, [fieldCode]: {...(facetRanges[fieldCode] ?? {from: "", to: ""}), [which]: value}};
        setFacetRanges(next);
        if (rangeTimer.current) clearTimeout(rangeTimer.current);
        rangeTimer.current = setTimeout(() => {
            applyFilters(selectedStatuses, selectedTypes, newestOnly, daysOldEnabled, daysOld, undefined, next);
        }, 400);
    };

    // THE FACETS FOLLOW THE LIST, not the other way round: they are recomputed when the type or
    // status narrowing changes, so each count describes the set the grid is actually showing.
    //
    // NOT WHEN A FACET ITSELF IS TICKED, deliberately. Narrowing by department and then recounting
    // departments under that narrowing would leave every other department reading zero and make the
    // filter impossible to widen again -- the pane would collapse to the one answer already chosen.
    useEffect(() => {
        if (!code) return;
        let cancelled = false;

        const load = async () => {
            try {
                const response = await facetApi.get("t1/v1/form_field_facets", {params: {
                    ObjectClassification: code,
                    ...(selectedTypes.size > 0
                        ? {ObjectTypeAcronyms:
                               withDescendants(statistics?.types ?? [], selectedTypes).join(",")}
                        : {}),
                    ...(selectedStatuses.size > 0
                        ? {Statuses: Array.from(selectedStatuses).join(",")} : {}),
                }});
                if (!cancelled) {
                    setFacets(response?.data?.Facets ?? []);
                    setFacetsAvailable(response?.data?.Available ?? 0);
                    setFacetsTruncated(!!response?.data?.Truncated);
                }
            } catch (e) {
                // NOT AN ERROR THE USER IS SHOWN. Facets decorate a list that works without them,
                // so a failure here leaves the pane as it was rather than taking over the page.
                if (!cancelled) { setFacets([]); setFacetsAvailable(0); setFacetsTruncated(false); }
            }
        };

        load();
        return () => { cancelled = true; };
    }, [facetApi, code, selectedStatuses, selectedTypes, statistics]);

    const toggleStatus = (status: string) => {
        const next = new Set(selectedStatuses);
        if (next.has(status)) next.delete(status);
        else next.add(status);
        setSelectedStatuses(next);
        applyFilters(next, selectedTypes, newestOnly, daysOldEnabled, daysOld);
    };

    const toggleType = (acronym: string) => {
        const next = new Set(selectedTypes);
        if (next.has(acronym)) next.delete(acronym);
        else next.add(acronym);
        setSelectedTypes(next);
        applyFilters(selectedStatuses, next, newestOnly, daysOldEnabled, daysOld);
    };

    const toggleNewest = () => {
        const next = !newestOnly;
        setNewestOnly(next);
        applyFilters(selectedStatuses, selectedTypes, next, daysOldEnabled, daysOld);
    };

    const toggleDaysOld = () => {
        const next = !daysOldEnabled;
        setDaysOldEnabled(next);
        applyFilters(selectedStatuses, selectedTypes, newestOnly, next, daysOld);
    };

    const changeDaysOld = (value: string) => {
        setDaysOld(value);
        // Only re-queries while the filter is actually on, so typing in the box before ticking it
        // costs nothing. An unusable value ("" mid-edit) resolves to no date condition rather than
        // to zero days, which would match nothing.
        if (daysOldEnabled) {
            applyFilters(selectedStatuses, selectedTypes, newestOnly, true, value);
        }
    };

    const clearFilters = () => {
        const empty = new Set<string>();
        setSelectedStatuses(empty);
        setSelectedTypes(new Set());
        setNewestOnly(false);
        setDaysOldEnabled(false);
        setDaysOld("");
        // AND THE FACETS. Clear has to clear everything the pane shows as ticked, or the grid stays
        // narrowed by something the user just told it to forget and the pane no longer shows.
        setSelectedFacets({});
        setFacetRanges({});
        applyFilters(empty, new Set(), false, false, "", {}, {});
    };

    const activeMode = sessionContext?.getActiveVisibility()?.Mode ?? "";
    const visibleModes = availableModes(activeMode, sessionContext.getPrivileges(), sessionContext.getAccount());

    const selectMode = (mode: string) => {
        const current = sessionContext?.getActiveVisibility();
        sessionContext?.setActiveVisibility(mode, current?.Value ?? "");
        EventBus.visibility.emit("change-visibility", sessionContext?.getActiveVisibility());
    };

    const toggleAskSelection = useCallback((usid: string) => {
        setAskSelection(prev => {
            const next = new Set(prev);
            if (next.has(usid)) next.delete(usid); else next.add(usid);
            return next;
        });
    }, []);

    // "ALL THE RECORDS IN THE LIST" MEANS THE PAGE IN FRONT OF YOU, and the toolbar says so in those
    // words. The grid holds forty rows of a list that may run to hundreds, and the honest reading of
    // "all" is what is on screen -- read from the EC's own buffer, the same rows the grid drew, so
    // the number on the button is the number that will be asked about.
    const selectAllShown = () => {
        const ec: any = objectsEO?.getEnterpriseComponent("ObjectRevision");
        const shown = prepareBuffer(ec) as any[];
        const usids = shown.map(row => (row?.get ? row.get("USID") : row?.USID))
                           .filter((one: any): one is string => !!one);
        setAskSelection(new Set(usids));
    };

    // The persona scopes which records resolve at all, so a selection made under the previous one
    // may name records this one cannot see. They would be dropped server-side and reported -- which
    // is correct but confusing, so the selection is cleared with the view it was made in.
    useEffect(() => {
        const onChangeVisibility = () => setAskSelection(new Set());
        EventBus.visibility.addListener("change-visibility", onChangeVisibility);
        return () => { EventBus.visibility.removeListener("change-visibility", onChangeVisibility); };
    }, []);

    // ONE BUTTON, IN THE GRID'S OWN HEADER, AND ALWAYS THERE. It used to render only once a row
    // was ticked, so the control that teaches you the feature exists did not appear until you had
    // already done the thing it was going to teach you -- and a line of prose ("Tick records to
    // ask the assistant about them") was carrying the whole discovery burden. Present and inert,
    // saying what it needs, is strictly better than absent.
    //
    // NO DEFAULT SCOPE, and that is forced rather than chosen. The grid shows FORTY rows and the
    // service reads at most twenty-five, so "ask about this page" would either be refused or
    // silently drop fifteen records -- and an answer about twenty-five of forty reads exactly like
    // an answer about forty. The scope has to be ticked.
    // WHAT WAS TICKED, AS RECORDS. The selection holds revision USIDs; a host making an edge needs
    // the logical object. Captured at the moment of ticking from the grid's buffer -- the only time
    // the row is certain to be on screen -- so a selection that spans pages keeps every record.
    const [picked, setPicked] = useState<Record<string, PickedRecord>>({});
    const toggleWithCapture = useCallback((usid: string) => {
        const ec: any = objectsEO?.getEnterpriseComponent("ObjectRevision");
        const rows = (prepareBuffer(ec) as any[]) ?? [];
        const row = rows.find(one => (one?.get ? one.get("USID") : one?.USID) === usid);
        const read = (name: string) => (row?.get ? row.get(name) : row?.[name]) ?? null;
        // A TICK IS A TICK ON A ROW THAT IS DRAWN. A click that arrives while the grid is still
        // swapping its rows in -- the page just turned, the search just ran -- names a revision the
        // buffer does not hold yet, and ticking it would show "1 selected" with no record behind
        // it: Select would then hand the host nothing and close (seen twice on 2026-10-28). So a
        // row the buffer cannot resolve is not ticked; the reader ticks it again once it is drawn.
        if (!row) return;
        setPicked(previous => ({...previous, [usid]: {
            RevisionUSID: usid, ObjectUSID: read("ObjectUSID"), ObjectID: read("ObjectID"),
            Name: read("Name"), Status: read("Status")}}));
        if (picker && !picker.multiple) {
            setAskSelection(previous => previous.has(usid) ? new Set() : new Set([usid]));
            return;
        }
        toggleAskSelection(usid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectsEO, picker]);

    useEffect(() => {
        if (!picker) return;
        picker.onSelectionChange(Array.from(askSelection).map(usid =>
            picked[usid] ?? {RevisionUSID: usid, ObjectUSID: null, ObjectID: null, Name: null, Status: null}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [askSelection, picked]);

    const ticked = askSelection.size;
    const overCap = ticked > MAX_ASK_RECORDS;
    const askButton = ticked > 0 && !overCap ? (
        <AskAboutAttachments
            scope={{kind: "Records", objectRevisionUSIDs: Array.from(askSelection)}}
            scopeLabel={`The attachments on ${ticked} selected `
                        + `${ticked === 1 ? classification.singular.toLowerCase()
                                          : classification.plural.toLowerCase()}`}
            note="Answered from the attachments on the selected records only."
        >
            {(open) => (
                <span onClick={open}
                      style={{fontSize: 13, color: Blue3, cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: 5}}>
                    <AutoAwesomeOutlined style={{fontSize: 16}}/>
                    Ask about {ticked} ticked
                </span>
            )}
        </AskAboutAttachments>
    ) : (
        // INERT, NOT HIDDEN, and the tooltip says which of the two reasons applies. Over the cap is
        // a different problem from nothing ticked, and a reader who has just ticked thirty rows
        // needs to be told the number rather than left to find out from a refusal.
        <Tooltip title={overCap
            ? `${ticked} records are ticked and at most ${MAX_ASK_RECORDS} can be read in one `
              + `question. Untick some, or ask the same question of a smaller set.`
            : `Tick the box at the left of each row -- up to ${MAX_ASK_RECORDS} -- or use the `
              + `checkbox in the column heading to take the whole page. Then this asks the `
              + `assistant about the files attached to them.`}>
            <span style={{fontSize: 13, color: overCap ? "#8A6D00" : Grey6,
                          display: "inline-flex", alignItems: "center", gap: 5,
                          cursor: "default"}}>
                <AutoAwesomeOutlined style={{fontSize: 16}}/>
                {overCap ? `${ticked} ticked — too many to ask` : "Ask about their attachments"}
            </span>
        </Tooltip>
    );

    return (
        // height 100%, NOT JUST flexGrow -- the same reason as the detail page. The shell hands this
        // page a fixed box that scrolls, and a BLOCK parent lets a flex child size to its content
        // instead of filling it. So the page was only ever as tall as the grid, and the filter
        // pane's separator painted to the height of the TABLE rather than the window: with the
        // grid empty ("No rows") it stopped a third of the way down, which reads as an unfinished
        // line rather than as a boundary between two columns.
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, height: "100%",
                     backgroundColor: White, minHeight: 0}}>
            {/* The bar carries the section name only; the trail sits under it, matching the Data
                Catalog where the bar reads "Enterprise Glossary" and the row below identifies the term. */}
            {!picker && (
                <PageHeader title={plural}>
                    <PersonaHeaderInfo/>
                </PageHeader>
            )}

            <div style={{display: "flex", flexDirection: "row", flexGrow: 1, minHeight: 0}}>
                <ObjectFilterSidebar
                    selectedStatuses={selectedStatuses}
                    onToggleStatus={toggleStatus}
                    statuses={statusVocabularyFor(code)}
                    selectedTypes={selectedTypes}
                    // A PROJECT HAS ONE OBJECT TYPE, by design (Ed, 2026-10-29: "there will only
                    // be one Object type for project"), so a Type section offering it would be a
                    // filter that cannot narrow anything. No toggle, no section.
                    onToggleType={code === "Project" ? undefined : toggleType}
                    newestOnly={newestOnly}
                    onToggleNewest={toggleNewest}
                    daysOldEnabled={daysOldEnabled}
                    onToggleDaysOld={toggleDaysOld}
                    daysOld={daysOld}
                    onDaysOldChange={changeDaysOld}
                    facets={facets}
                    facetsAvailable={facetsAvailable}
                    facetsTruncated={facetsTruncated}
                    selectedFacets={selectedFacets}
                    onToggleFacet={toggleFacet}
                    facetRanges={facetRanges}
                    onFacetRangeChange={changeFacetRange}
                    statistics={statistics}
                    onClear={clearFilters}
                />

                {/* Content */}
                <div style={{flex: 1, overflow: "auto", padding: "0 30px", display: "flex", flexDirection: "column", minHeight: 0}}>
                    {/* Section header: tinted icon tile + name, ruled off underneath. Same treatment
                        as the Data Catalog's Catalog Items and Enterprise Glossary headers. */}
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #E4E7EC"}}>
                        <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: 12}}>
                            <Box sx={{width: 34, height: 34, borderRadius: "6px", backgroundColor: "rgba(20,152,216,0.12)", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                <Icon sx={{color: Blue3, fontSize: 20}}/>
                            </Box>
                            <Typography style={{fontSize: 20, fontWeight: 500, color: Grey17}}>{plural}</Typography>
                        </div>
                        {/* Offered only to holders of <type>:create, which the server enforces on the POST. */}
                        {!picker && hasPrivilege(objectPrivilegeCode(classification, "create")) ? (
                            <CCButton className={css_buttons.blueStandardButton} onClick={() => setCreating(true)}>New {classification.singular.toLowerCase()}</CCButton>
                        ) : null}
                    </div>

                    {/* Visibility-mode selector. The lifecycle tabs that used to sit opposite it were
                        removed in favour of the sidebar's Status filters.

                        NOT DRAWN WHEN THERE IS NOTHING TO CHOOSE. availableModes deliberately
                        returns [] for somebody with one persona and no widening grants -- a lone
                        tab describes the only view its owner will ever get, which reads as broken
                        rather than as nothing to decide. The container went on rendering anyway,
                        with its bottom border and top margin, so the page carried an empty ruled
                        band under the header: a second line with nothing in it. Reported by Ed
                        against Documents, and it is exactly what a new tenant's team member sees,
                        because one position and no grants is the ordinary case there. */}
                    {/* THE VIEW AND THE SEARCH ON ONE LINE. The tabs occupied about 340px of a
                        1340px band and everything right of them was dead space, while an 85px
                        bordered search panel sat immediately beneath repeating the persona those
                        tabs had just stated -- the third statement of it on one screen, after the
                        blue header. Putting the box here reclaims the band, removes the
                        triplication, and makes the relationship literal: the tab sets the view,
                        the box searches inside it.

                        AND THE BAND IS NO LONGER CONDITIONAL. It used to be dropped entirely when
                        availableModes returned [] -- somebody with one persona and no widening
                        grants, which is the ordinary case in a new tenant -- because an empty
                        ruled line reads as broken. It is not empty now: with no tabs to draw it
                        still carries the search, so dropping it would take the search away from
                        exactly the readers who have the fewest other ways to find anything.

                        flexWrap, because four tabs and a 340px input will not share a narrow
                        window; below that width the input takes its own line rather than
                        colliding. */}
                    <div style={{display: "flex", flexWrap: "wrap", alignItems: "flex-end",
                                 justifyContent: "space-between", gap: 12,
                                 borderBottom: `1px solid ${Grey2}`, marginTop: 16}}>
                        <div style={{display: "flex", gap: 22}}>
                            {visibleModes.map(m => (
                                <div key={m.mode} onClick={() => selectMode(m.mode)} style={tabStyle(activeMode === m.mode)}>
                                    {m.label}
                                </div>
                            ))}
                        </div>
                        {/* TWO BOXES, RECORDS FIRST. The left one searches the records; the right
                            one searches inside the files attached to them. Records first because
                            it is the question a reader arrives with, and because it is the one
                            that answers for every classification rather than only those whose
                            files have been read. */}
                        <div style={{display: "flex", alignItems: "flex-end", flexWrap: "wrap",
                                     gap: 18, marginBottom: 4}}>
                            <SearchRecords enterpriseComponent={objectsEO?.getEnterpriseComponent("ObjectRevision")}
                                           classificationPlural={classification.plural}/>
                            <SearchAttachments classificationCode={code}
                                               classificationPlural={classification.plural}/>
                        </div>
                    </div>

                    {/* The grid. 40 rows by default, set on the applet.
                        THE GRID TITLES ITSELF ONLY WHEN NOTHING ABOVE IT DOES. Passing `plural` here
                        put the classification on screen a THIRD time -- the blue bar names it, the
                        section header right above names it again with the icon tile and the New
                        button, and then the grid repeated it with a count. Reported by Ed against
                        Documents: "There are two lines at the top."
                        THAT ESCAPE HATCH NEVER WORKED. `title={null}` is PlatformTable's own way
                        of suppressing the icon-and-name block, but the applet defaulted it with
                        `this.props.title ?? "Records"` -- and `??` treats null as absent. So the
                        block rendered anyway, under the generic word: Ed, 2026-09-21, "on the
                        Document page the table is called Records. It should be Documents."

                        NAMED FROM THE REGISTRY rather than suppressed, so the grid says what it
                        holds and says it the same way every other surface does. One word, one
                        source: a classification renamed there is renamed here. */}
                    {/* THE SELECTION TOOLBAR, above the grid and only once something is ticked.
                        Drawn empty it would be a permanent band saying "0 selected", which is a
                        control describing its own disuse. The count is the number of records, and
                        the panel it opens says the same number again -- a question asked of four
                        documents should never be presentable as an answer about the list. */}
                    <div style={{margin: "30px 0", display: "flex", flexDirection: "column"}}>
                        {/* SELECTION MANAGEMENT ONLY -- the ask itself lives in the grid's own
                            header, where a list ACTION belongs. But "Select all shown" is NOT
                            hidden until something is selected, and the first version of this
                            wrapped it in the same condition as the count: the one-click path to
                            making a selection appeared only once you had made one by hand. That is
                            the exact fault this evening was spent removing, reintroduced in the
                            act of removing it. The count and Clear are conditional because they
                            describe a selection; the way to START one never is. */}
                        <div style={{display: "flex", alignItems: "center", gap: 14,
                                     minHeight: 24, marginBottom: 6}}>
                            {askSelection.size > 0 ? (
                                <Typography style={{fontSize: 13, color: Grey6}}>
                                    {askSelection.size} selected
                                </Typography>
                            ) : null}
                            <span onClick={selectAllShown}
                                  style={{fontSize: 13, color: Blue3, cursor: "pointer"}}>
                                Select all shown
                            </span>
                            {askSelection.size > 0 ? (
                                <span onClick={() => setAskSelection(new Set())}
                                      style={{fontSize: 13, color: Grey6, cursor: "pointer"}}>
                                    Clear
                                </span>
                            ) : null}
                        </div>
                        <ObjectRevisionTableApplet key={code} title={classification.plural}
                                                   classification={code}
                                                   enterpriseComponent={objectsEO?.getEnterpriseComponent("ObjectRevision")}
                                                   selectable={true}
                                                   actions={picker ? null : askButton}
                                                   onSelectAllShown={selectAllShown}
                                                   onClearSelection={() => setAskSelection(new Set())}
                                                   selectedUSIDs={askSelection}
                                                   onToggleSelect={picker ? toggleWithCapture : toggleAskSelection}
                                                   pickOnClick={!!picker}/>
                    </div>
                </div>
            </div>

            {/* Creating changes both the grid and the counts, so the sidebar is refreshed too --
                otherwise the new draft appears in the list while "Drafts" still reads the old
                number. */}
            {/* A PROJECT HAS ITS OWN DIALOG: who it is for, and what it is (Ed, 2026-10-29). See
                CreateProject. Every other classification creates through the generic one. */}
            {code === "Project" ? (
                <CreateProject
                    open={creating}
                    onClose={() => setCreating(false)}
                    route={classification.route}
                    onCreated={() => {
                        objectsEO?.getEnterpriseComponent("ObjectRevision")?.invalidate();
                        refreshStatistics();
                    }}
                />
            ) : (
                <CreateObject
                    open={creating}
                    onClose={() => setCreating(false)}
                    classification={code}
                    singular={classification.singular}
                    route={classification.route}
                    offeredOnObjectTypes={classification.offeredOnObjectTypes}
                    onCreated={() => {
                        objectsEO?.getEnterpriseComponent("ObjectRevision")?.invalidate();
                        refreshStatistics();
                    }}
                />
            )}
        </div>
    );
}
